import React, { useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { Card, Table, Button, Modal, Form, Input, InputNumber, Tag, Space, Typography, Image, Popconfirm, Select, Tabs, Switch, Alert, Divider } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import { apiService } from '../../../services/api';
import { AdvertisementPosition, AdvertisementSlotResponse, AdvertisementRequestResponse, AdvertisementType, AdvertisementRequestStatus, WeekAvailabilityInfo } from '../../../types';
import { getAdvertisementPageLabel, getAdvertisementPositionLabel, normalizeAdvertisementPagePath } from '../../../utils/advertisementSlots';
import { getAdvertisementImageSrc } from '../../../utils/advertisementImage';
const { Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const AdminAdvertisementTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [slotModalVisible, setSlotModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AdvertisementRequestResponse | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AdvertisementSlotResponse | null>(null);
  const [slotForm] = Form.useForm();
  const [approveForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('slots');
  const [previewWeeks, setPreviewWeeks] = useState<WeekAvailabilityInfo[]>([]);

  const requestStatus = activeTab === 'pending' ? AdvertisementRequestStatus.Pending :
    activeTab === 'approved' ? AdvertisementRequestStatus.Approved :
    activeTab === 'rejected' ? AdvertisementRequestStatus.Rejected : undefined;

  const { data: slots = [], isLoading: slotsLoading } = useQuery({
    queryKey: adminQueryKeys.advertisementSlots,
    queryFn: async () => {
      const r = await apiService.getAdminAdvertisementSlots();
      if (!r.success) throw new Error(r.message);
      return (r.data ?? []) as AdvertisementSlotResponse[];
    },
    enabled: activeTab === 'slots',
    staleTime: 1000 * 60 * 2,
  });

  const { data: requests = [], isLoading: requestsLoading } = useQuery({
    queryKey: adminQueryKeys.advertisementRequests(requestStatus),
    queryFn: async () => {
      const r = await apiService.getAdminAdvertisementRequests(requestStatus);
      if (!r.success) throw new Error(r.message);
      return (r.data ?? []) as AdvertisementRequestResponse[];
    },
    enabled: activeTab !== 'slots',
    staleTime: 1000 * 60 * 2,
  });

  const loading = activeTab === 'slots' ? slotsLoading : requestsLoading;

  const normalizeSlotPayload = (values: any) => ({
    ...values,
    pagePath: values.pagePath ? normalizeAdvertisementPagePath(values.pagePath) : values.pagePath,
  });

  const invalidateAds = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-panel', 'advertisement-slots'] });
    queryClient.invalidateQueries({ queryKey: ['admin-panel', 'advertisement-requests'] });
  };

  const createSlotMutation = useMutation({
    mutationFn: (values: any) => apiService.createAdvertisementSlot(values),
    onSuccess: (res) => {
      if (res.success) {
        setSlotModalVisible(false);
        slotForm.resetFields();
        setSelectedSlot(null);
        invalidateAds();
        toast.success('Reklam slotu başarıyla oluşturuldu!');
      } else toast.error(res.message || 'Reklam slotu oluşturulurken hata oluştu.');
    },
    onError: () => toast.error('Reklam slotu oluşturulurken hata oluştu.'),
  });

  const updateSlotMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: any }) => apiService.updateAdvertisementSlot(id, values),
    onSuccess: (res) => {
      if (res.success) {
        setSlotModalVisible(false);
        slotForm.resetFields();
        setSelectedSlot(null);
        invalidateAds();
        toast.success('Reklam slotu başarıyla güncellendi!');
      } else toast.error(res.message || 'Reklam slotu güncellenirken hata oluştu.');
    },
    onError: () => toast.error('Reklam slotu güncellenirken hata oluştu.'),
  });

  const deleteSlotMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteAdvertisementSlot(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Reklam slotu silindi.');
        invalidateAds();
      } else toast.error(res.message || 'Reklam slotu silinirken hata oluştu.');
    },
    onError: () => toast.error('Reklam slotu silinirken hata oluştu.'),
  });

  const approveRequestMutation = useMutation({
    mutationFn: ({ id, weekStart }: { id: number; weekStart: string }) =>
      apiService.approveAdvertisementRequest(id, weekStart),
    onSuccess: (res) => {
      if (res.success) {
        setApproveModalVisible(false);
        approveForm.resetFields();
        setSelectedRequest(null);
        invalidateAds();
        toast.success('Reklam talebi onaylandı!');
      } else toast.error(res.message || 'Reklam talebi onaylanırken hata oluştu.');
    },
    onError: () => toast.error('Reklam talebi onaylanırken hata oluştu.'),
  });

  const rejectRequestMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => apiService.rejectAdvertisementRequest(id, reason),
    onSuccess: (res) => {
      if (res.success) {
        setRejectModalVisible(false);
        rejectForm.resetFields();
        setSelectedRequest(null);
        invalidateAds();
        toast.success('Reklam talebi reddedildi.');
      } else toast.error(res.message || 'Reklam talebi reddedilirken hata oluştu.');
    },
    onError: () => toast.error('Reklam talebi reddedilirken hata oluştu.'),
  });

  const handleCreateSlot = (values: any) => {
    createSlotMutation.mutate(normalizeSlotPayload(values));
  };

  const handleUpdateSlot = (values: any) => {
    if (selectedSlot) updateSlotMutation.mutate({ id: selectedSlot.id, values: normalizeSlotPayload(values) });
  };

  const handleDeleteSlot = (id: number) => {
    deleteSlotMutation.mutate(id);
  };

  const handleApproveRequest = () => {
    if (selectedRequest) {
      approveRequestMutation.mutate({ id: selectedRequest.id, weekStart: selectedRequest.requestedWeekStartDate });
    }
  };

  const handleRejectRequest = (values: any) => {
    if (selectedRequest) {
      rejectRequestMutation.mutate({ id: selectedRequest.id, reason: values.rejectionReason });
    }
  };

  const getStatusColor = (status: AdvertisementRequestStatus) => {
    switch (status) {
      case AdvertisementRequestStatus.Pending:
        return 'orange';
      case AdvertisementRequestStatus.Approved:
        return 'green';
      case AdvertisementRequestStatus.Rejected:
        return 'red';
      default:
        return 'default';
    }
  };

  const slotColumns = [
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      fixed: 'left' as const,
      render: (type: AdvertisementType) => (
        <Tag color={type === AdvertisementType.Banner ? 'blue' : 'purple'}>
          {type === AdvertisementType.Banner ? 'Banner' : 'Card'}
        </Tag>
      )
    },
    {
      title: 'Ad',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      ellipsis: true
    },
    {
      title: 'Sayfa',
      dataIndex: 'pagePath',
      key: 'pagePath',
      width: 120,
      responsive: ['md'] as any,
      ellipsis: true,
      render: (path: string) => getAdvertisementPageLabel(path)
    },
    {
      title: 'Alan',
      dataIndex: 'position',
      key: 'position',
      width: 100,
      responsive: ['md'] as any,
      render: (position: AdvertisementPosition) => (
        <Tag>{getAdvertisementPositionLabel(position)}</Tag>
      )
    },
    {
      title: 'Ücret',
      dataIndex: 'price',
      key: 'price',
      width: 90,
      render: (price: number) => `₺${price.toFixed(2)}`
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'Bugün',
      dataIndex: 'isAvailable',
      key: 'isAvailable',
      width: 90,
      responsive: ['lg'] as any,
      render: (isAvailable: boolean) => (
        <Tag color={isAvailable ? 'green' : 'orange'}>
          {isAvailable ? 'Müsait' : 'Dolu'}
        </Tag>
      )
    },
    {
      title: 'Müsait Günler',
      key: 'availableWeeks',
      width: 120,
      responsive: ['lg'] as any,
      render: (record: AdvertisementSlotResponse) => {
        const availableCount = record.availableWeeks?.filter(w => w.isAvailable).length || 0;
        const totalDays = record.availableWeeks?.length || 0;
        return (
          <Text>
            {availableCount} / {totalDays} gün
          </Text>
        );
      }
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      width: 150,
      responsive: ['xl'] as any,
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (record: AdvertisementSlotResponse) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedSlot(record);
              slotForm.setFieldsValue({
                type: record.type,
                position: record.position,
                name: record.name,
                description: record.description,
                pagePath: normalizeAdvertisementPagePath(record.pagePath),
                price: record.price,
                isActive: record.isActive
              });
              setSlotModalVisible(true);
            }}
            size="small"
            title="Düzenle"
          />
          {record.isAvailable && (
            <Popconfirm
              title="Bu slotu silmek istediğinize emin misiniz?"
              onConfirm={() => handleDeleteSlot(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                title="Sil"
              />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const requestColumns = [
    {
      title: 'Mentör',
      dataIndex: 'mentorName',
      key: 'mentorName',
      width: 120,
      fixed: 'left' as const,
      ellipsis: true
    },
    {
      title: 'Slot',
      dataIndex: 'slotName',
      key: 'slotName',
      width: 150,
      ellipsis: true,
      render: (text: string, record: AdvertisementRequestResponse) => (
        <Space direction="vertical" size="small">
          <Space>
            <Tag color={record.slotType === AdvertisementType.Banner ? 'blue' : 'purple'}>
              {record.slotType === AdvertisementType.Banner ? 'Banner' : 'Card'}
            </Tag>
              <Tag>{getAdvertisementPositionLabel(record.slotPosition)}</Tag>
            <Text>{text}</Text>
          </Space>
          {record.requestedWeekLabel && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.requestedWeekLabel}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Resim',
      key: 'image',
      width: 80,
      responsive: ['md'] as any,
      render: (record: AdvertisementRequestResponse) => (
        <Image
          width={60}
          height={40}
          src={getAdvertisementImageSrc(record.imageBase64, record.imageContentType)}
          style={{ objectFit: 'cover', borderRadius: '4px' }}
        />
      )
    },
    {
      title: 'Ücret',
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      width: 90,
      render: (amount: number) => amount ? `₺${amount.toFixed(2)}` : '-'
    },
    {
      title: 'Ödeme',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'Completed' ? 'green' : status === 'Failed' ? 'red' : 'orange'}>
          {status === 'Completed' ? 'Tamamlandı' : status === 'Failed' ? 'Başarısız' : 'Bekliyor'}
        </Tag>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: AdvertisementRequestStatus, record: AdvertisementRequestResponse) => (
        <Tag color={getStatusColor(status)}>{record.statusName}</Tag>
      )
    },
    {
      title: 'Oluşturulma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      responsive: ['lg'] as any,
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (record: AdvertisementRequestResponse) => (
        <Space size="small">
          {record.status === AdvertisementRequestStatus.Pending && (
            <>
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={() => {
                  setSelectedRequest(record);
                  setApproveModalVisible(true);
                }}
                size="small"
                style={{ color: 'green' }}
                title="Onayla"
              />
              <Button
                type="text"
                danger
                icon={<CloseOutlined />}
                onClick={() => {
                  setSelectedRequest(record);
                  rejectForm.resetFields();
                  setRejectModalVisible(true);
                }}
                size="small"
                title="Reddet"
              />
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="panel-tab-content">
      <TabListLoading loading={loading}>
      <Card className="panel-table-card" style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Reklam Slotları" key="slots">
            <div style={{ marginBottom: '16px' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedSlot(null);
                  slotForm.resetFields();
                  const days: WeekAvailabilityInfo[] = [];
                  const start = dayjs().startOf('day');
                  for (let i = 0; i < 60; i++) {
                    const d = start.add(i, 'day');
                    days.push({
                      weekStartDate: d.format('YYYY-MM-DD'),
                      weekEndDate: d.endOf('day').toISOString(),
                      isAvailable: true,
                      weekLabel: d.locale('tr').format('D MMMM YYYY dddd')
                    });
                  }
                  setPreviewWeeks(days);
                  setSlotModalVisible(true);
                }}
              >
                Yeni Slot Oluştur
              </Button>
            </div>
            <Table
              scroll={{ x: 'max-content' }}
              columns={slotColumns}
              dataSource={slots}
              rowKey="id"
              loading={loading}
              size="middle"
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Toplam ${total} slot`,
                style: { marginTop: '16px' },
                responsive: true,
                simple: window.innerWidth < 768
              }}
            />
          </TabPane>
          <TabPane tab="Bekleyen Talepler" key="pending">
            <Table
              scroll={{ x: 'max-content' }}
              columns={requestColumns}
              dataSource={requests.filter(r => r.status === AdvertisementRequestStatus.Pending)}
              rowKey="id"
              loading={loading}
              size="middle"
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Toplam ${total} talep`,
                style: { marginTop: '16px' },
                responsive: true,
                simple: window.innerWidth < 768
              }}
            />
          </TabPane>
          <TabPane tab="Onaylanan Talepler" key="approved">
            <Table
              scroll={{ x: 'max-content' }}
              columns={requestColumns}
              dataSource={requests.filter(r => r.status === AdvertisementRequestStatus.Approved)}
              rowKey="id"
              loading={loading}
              size="middle"
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Toplam ${total} talep`,
                style: { marginTop: '16px' },
                responsive: true,
                simple: window.innerWidth < 768
              }}
            />
          </TabPane>
          <TabPane tab="Reddedilen Talepler" key="rejected">
            <Table
              scroll={{ x: 'max-content' }}
              columns={requestColumns}
              dataSource={requests.filter(r => r.status === AdvertisementRequestStatus.Rejected)}
              rowKey="id"
              loading={loading}
              size="middle"
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Toplam ${total} talep`,
                style: { marginTop: '16px' },
                responsive: true,
                simple: window.innerWidth < 768
              }}
            />
          </TabPane>
        </Tabs>
      </Card>
      </TabListLoading>

      {/* Slot Modal */}
      <Modal
        title={selectedSlot ? 'Slot Düzenle' : 'Yeni Slot Oluştur'}
        open={slotModalVisible}
        onCancel={() => {
          setSlotModalVisible(false);
          slotForm.resetFields();
          setSelectedSlot(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={slotForm}
          layout="vertical"
          onFinish={(values) => {
            if (selectedSlot) {
              handleUpdateSlot(values);
            } else {
              handleCreateSlot(values);
            }
          }}
        >
          <Form.Item
            label="Tip"
            name="type"
            rules={[{ required: true, message: 'Lütfen bir tip seçin' }]}
          >
            <Select placeholder="Reklam tipi seçin">
              <Select.Option value={AdvertisementType.Banner}>Banner</Select.Option>
              <Select.Option value={AdvertisementType.Card}>Card</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Alan"
            name="position"
            rules={[{ required: true, message: 'Lütfen bir alan seçin' }]}
          >
            <Select placeholder="Reklamin yerlesecegi alani secin">
              <Select.Option value={AdvertisementPosition.Top}>Ust</Select.Option>
              <Select.Option value={AdvertisementPosition.Inline}>Satir Ici</Select.Option>
              <Select.Option value={AdvertisementPosition.Bottom}>Alt</Select.Option>
              <Select.Option value={AdvertisementPosition.Sidebar}>Kenar</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Ad"
            name="name"
            rules={[{ required: true, message: 'Lütfen bir ad girin' }]}
          >
            <Input placeholder="Slot adı" />
          </Form.Item>

          <Form.Item
            label="Açıklama"
            name="description"
          >
            <TextArea rows={3} placeholder="Slot açıklaması" />
          </Form.Item>

          <Form.Item
            label="Sayfa"
            name="pagePath"
            rules={[{ required: true, message: 'Lütfen bir sayfa seçin' }]}
          >
            <Select placeholder="Reklamın gösterileceği sayfayı seçin">
              <Select.Option value="/">Ana Sayfa</Select.Option>
              <Select.Option value="/mentor-search">Mentör Arama</Select.Option>
              <Select.Option value="/mentees">Mentee Listesi</Select.Option>
              <Select.Option value="/events">Etkinlikler</Select.Option>
              <Select.Option value="/job-postings">İş İlanları</Select.Option>
              <Select.Option value="/categories">Kategoriler</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Ücret (TL)"
            name="price"
            rules={[{ required: true, message: 'Lütfen bir ücret girin' }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="Günlük reklam ücreti"
            />
          </Form.Item>

          <Form.Item
            label="Aktif"
            name="isActive"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          {!selectedSlot && (
            <>
              <Divider />
              <Alert
                message="Günlük kiralama"
                description="Slot için müsaitlik, önümüzdeki 60 gün ve Türkiye takvimi ile hesaplanır. Aynı slotta aynı gün yalnızca bir aktif/pending talep olabilir."
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: '4px', padding: '12px' }}>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>Önümüzdeki 60 gün (örnek önizleme):</Text>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {previewWeeks.length > 0 ? (
                    previewWeeks.map((week, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                        <Text style={{ fontSize: '12px' }}>{week.weekLabel}</Text>
                        <Tag color="green" style={{ margin: 0 }}>Müsait</Tag>
                      </div>
                    ))
                  ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>Slot oluşturulduktan sonra gün listesi API üzerinden hesaplanır.</Text>
                  )}
                </Space>
              </div>
            </>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedSlot ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => {
                setSlotModalVisible(false);
                slotForm.resetFields();
                setSelectedSlot(null);
                setPreviewWeeks([]);
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Approve Modal */}
      <Modal
        title="Reklam Talebini Onayla"
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
          approveForm.resetFields();
          setSelectedRequest(null);
        }}
        footer={null}
        width={500}
      >
        {selectedRequest && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Yayın günü: </Text>
              <Text>{selectedRequest.requestedWeekLabel}</Text>
            </div>
            <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
              Reklam yalnızca seçilen takvim günü boyunca yayınlanır.
            </Text>

            <Form.Item>
              <Space>
                <Button type="primary" onClick={handleApproveRequest}>
                  Onayla
                </Button>
                <Button onClick={() => {
                  setApproveModalVisible(false);
                  approveForm.resetFields();
                  setSelectedRequest(null);
                }}>
                  İptal
                </Button>
              </Space>
            </Form.Item>
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reklam Talebini Reddet"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          rejectForm.resetFields();
          setSelectedRequest(null);
        }}
        footer={null}
        width={500}
      >
        <Form
          form={rejectForm}
          layout="vertical"
          onFinish={handleRejectRequest}
        >
          <Form.Item
            label="Red Sebebi (Opsiyonel)"
            name="rejectionReason"
          >
            <TextArea rows={4} placeholder="Red sebebini açıklayın..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" danger htmlType="submit">
                Reddet
              </Button>
              <Button onClick={() => {
                setRejectModalVisible(false);
                rejectForm.resetFields();
                setSelectedRequest(null);
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminAdvertisementTab;

